import { CheckCircleFilled } from '@ant-design/icons';
import { Col, Divider, Progress, Row, Tooltip } from 'antd';
import DOMPurify from 'dompurify';
import React, { useState } from 'react';

import { useRole } from '../authentication/hooks/useRole';
import type {
  LinkedVariant,
  StageProgress,
  VariantReportData,
} from '../probands_samples/types';
import { formatVariantHgvs } from '../probands_samples/utils';
import SectionEditBtn from '../variant_workflow/components/SectionEditBtn';
import { WORKFLOW_EDIT_ROLES } from '../variant_workflow/hooks/useVariantAccess';
import SampleDetailDiagnosticPanelModal from './SampleDetailDiagnosticPanelModal';
import SampleDetailEditModal from './SampleDetailEditModal';
import SampleDetailReportCategoryModal from './SampleDetailReportCategoryModal';
import type { SampleDetail } from './types';

type SampleDetailMainSectionProps = {
  sample?: SampleDetail;
};

type SampleDetailMetadataTableProps = {
  sample?: SampleDetail;
};

const renderHtmlValue = (value?: string | null) => {
  if (!value) {
    return null;
  }

  return (
    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />
  );
};

const SampleDetailVariantsTable = ({
  sample,
}: SampleDetailMetadataTableProps) => {
  const { hasRole } = useRole();
  const canEditSample =
    hasRole(WORKFLOW_EDIT_ROLES) && sample?.can_edit !== false;
  const variants = sample?.genotype_variants ?? [];
  const [selectedPanelVariant, setSelectedPanelVariant] = useState<
    LinkedVariant | undefined
  >();
  const [selectedReportCategory, setSelectedReportCategory] = useState<{
    reportData: VariantReportData;
    variant: LinkedVariant;
  }>();

  return (
    <>
      <div className="sample-detail__variants-table">
        <table className="sample-detail__variants-table-content">
          <thead>
            <tr className="sample-detail__variants-header">
              <th>Variants</th>
              <th>Progress</th>
              <th>Clinical interpretation</th>
              <th>Zygosity</th>
              <th>Selected diagnostic panel for report</th>
              <th>Report Category</th>
            </tr>
          </thead>
          <tbody>
            {variants.map((variantData) => {
              const variantPath = variantData.variant_id
                ? `/variant-workflow/${variantData.variant_id}`
                : undefined;

              return (
                <tr
                  className="sample-detail__variants-row"
                  key={variantData.genotype_id}
                >
                  <td>
                    {variantPath ? (
                      <a
                        href={variantPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sample-detail__variants-link"
                      >
                        {formatVariantHgvs(variantData)}
                      </a>
                    ) : (
                      <span>{formatVariantHgvs(variantData)}</span>
                    )}
                  </td>
                  <td>
                    {variantData.progress && variantData.progress.total > 0 ? (
                      <VariantProgressBadge progress={variantData.progress} />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {variantData.clinical_interpretation || 'to be classified'}
                  </td>
                  <td>{variantData.zygosity}</td>
                  <td>
                    <button
                      className="sample-detail__variants-panel-cell"
                      type="button"
                      disabled={!canEditSample}
                      onClick={() => setSelectedPanelVariant(variantData)}
                    >
                      {variantData.report_data?.map((reportData) => (
                        <span
                          className="sample-detail__variants-muted-link"
                          key={reportData.id}
                        >
                          {reportData.medicalinvestigation_name}
                        </span>
                      ))}
                    </button>
                  </td>
                  <td>
                    {variantData.report_data?.map((reportData) => (
                      <div key={reportData.id}>
                        <button
                          className="sample-detail__variants-text-button"
                          type="button"
                          disabled={!canEditSample}
                          onClick={() =>
                            setSelectedReportCategory({
                              reportData,
                              variant: variantData,
                            })
                          }
                        >
                          {reportData.report_category}
                        </button>
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <SampleDetailDiagnosticPanelModal
        open={Boolean(selectedPanelVariant)}
        sample={sample}
        variant={selectedPanelVariant}
        onClose={() => setSelectedPanelVariant(undefined)}
      />
      <SampleDetailReportCategoryModal
        open={Boolean(selectedReportCategory)}
        reportData={selectedReportCategory?.reportData}
        sample={sample}
        variant={selectedReportCategory?.variant}
        onClose={() => setSelectedReportCategory(undefined)}
      />
    </>
  );
};

const SampleDetailMetadataTable = ({
  sample,
}: SampleDetailMetadataTableProps) => {
  const fields = [
    { label: 'Indication', value: renderHtmlValue(sample?.indication) },
    { label: 'Reporter', value: sample?.reporter.display_name },
    { label: 'Entry date', value: sample?.created },
    { label: 'Proband id', value: sample?.subject.externalid },
    { label: 'Comment', value: renderHtmlValue(sample?.comment) },
  ] as const;

  return (
    <div className="sample-detail__metadata-table">
      {fields.map(({ label, value }) => (
        <React.Fragment key={label}>
          <Row className="sample-detail__field-row">
            <Col flex="15%" className="sample-detail__field-label">
              {label}
            </Col>
            <Col flex="85%" className="sample-detail__field-value">
              {value}
            </Col>
          </Row>
          <Divider className="sample-detail__field-divider" />
        </React.Fragment>
      ))}
    </div>
  );
};

const VariantProgressBadge = ({ progress }: { progress: StageProgress }) => {
  const pct = progress.percentage;
  const isComplete = pct === 100;
  const statusClass = isComplete ? 'complete' : pct >= 50 ? 'active' : 'early';
  return (
    <Tooltip
      title={`${progress.completed} of ${progress.total} stages accepted`}
    >
      <div
        className={`sample-detail__progress-badge sample-detail__progress-badge--${statusClass}`}
      >
        <div className="sample-detail__progress-ring">
          <svg viewBox="0 0 36 36" className="sample-detail__progress-svg">
            <path
              className="sample-detail__progress-track"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              strokeWidth="3"
            />
            <path
              className="sample-detail__progress-fill"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              strokeWidth="3"
              strokeDasharray={`${pct}, 100`}
              strokeLinecap="round"
            />
          </svg>
          <span className="sample-detail__progress-pct">
            {isComplete ? <CheckCircleFilled /> : `${pct}%`}
          </span>
        </div>
        <div className="sample-detail__progress-info">
          <span className="sample-detail__progress-count">
            {progress.completed}/{progress.total}
          </span>
        </div>
      </div>
    </Tooltip>
  );
};

const SampleDetailProgressBar = ({
  sample,
}: SampleDetailMetadataTableProps) => {
  const progress = sample?.interpretation_progress;
  if (!progress || progress.total === 0) {
    return null;
  }

  return (
    <div className="sample-detail__progress">
      <span className="sample-detail__progress-text">Progress</span>
      <Tooltip
        title={`${progress.completed} of ${progress.total} variants accepted`}
      >
        <Progress
          percent={progress.percentage}
          size="small"
          strokeColor="#45606b"
          format={() => (
            <strong>{`${progress.completed}/${progress.total}`}</strong>
          )}
          className="sample-detail__progress-bar"
        />
      </Tooltip>
    </div>
  );
};

const SampleDetailMainSection = ({ sample }: SampleDetailMainSectionProps) => {
  const { hasRole } = useRole();
  const canEditSample =
    hasRole(WORKFLOW_EDIT_ROLES) && sample?.can_edit !== false;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  return (
    <div className="sample-detail__main-section">
      <SectionEditBtn
        onClick={() => setIsEditModalOpen(true)}
        disabled={!canEditSample}
      />
      <SampleDetailMetadataTable sample={sample} />
      <SampleDetailProgressBar sample={sample} />
      <SampleDetailVariantsTable sample={sample} />
      <SampleDetailEditModal
        open={isEditModalOpen}
        sample={sample}
        onClose={() => setIsEditModalOpen(false)}
      />
    </div>
  );
};

export default SampleDetailMainSection;
